"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Send, PhoneCall } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
interface FormDataState {
  name: string;
  phone: string;
  email: string;
  address: string;
  project_type: string;
  timing: string;
  budget: string;
  contact_method: string;
  details: string;
  consent: boolean;
}
export default function RequestEstimate() {
  const [formData, setFormData] = useState<FormDataState>({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    timing: "",
    budget: "",
    contact_method: "",
    details: "",
    consent: false
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const projectTypeOptions = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
  const timingOptions = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
  const budgetOptions = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
  const contactMethodOptions = ["Phone", "Text", "Email"];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: keyof FormDataState, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    if (!formData.consent) {
      setErrorMessage("Please accept the contact agreement to continue.");
      return;
    }
    setStatus("submitting");
    try {
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
      const params = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        project_type: formData.project_type,
        timing: formData.timing,
        budget: formData.budget,
        contact_method: formData.contact_method,
        details: formData.details,
        website: "DecaturHomeRenovation.com",
        page: currentPath,
        lead_source: "organic"
      });
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setStatus("error");
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    }
  };
  return <section id="requestestimate" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center justify-center gap-2 mb-3">
            <span className="h-px w-8 bg-border" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Direct Consultation
            </span>
            <span className="h-px w-8 bg-border" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif tracking-tight font-normal text-foreground mb-4">
            Request an Estimate
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Tell us about your home renovation plans. We review every inquiry personally to prepare your tailored preliminary scope.
          </p>
        </motion.div>

        {/* Card Container */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-card/90 backdrop-blur-md border border-border rounded-xl shadow-lg p-6 sm:p-10 lg:p-12 relative">
          <AnimatePresence mode="wait">
            {status === "success" ? <motion.div key="success-view" initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: 0.3
          }} className="py-12 sm:py-16 text-center max-w-md mx-auto">
                <div className="size-16 rounded-full bg-secondary text-foreground flex items-center justify-center mx-auto mb-6 shadow-xs border border-border">
                  <CheckCircle2 className="size-8 stroke-[1.75]" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif text-foreground mb-3 font-normal">
                  Request Received
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-8">
                  Thank you for reaching out. Our team will review your project requirements and follow up via your preferred contact method within one business day.
                </p>
                <div className="inline-flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground bg-muted/60 px-4 py-2 rounded-full border border-border">
                  <PhoneCall className="size-3.5" />
                  <span>Direct inquiries: (470) 847-9076</span>
                </div>
              </motion.div> : <motion.form key="form-view" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                {status === "error" && errorMessage && <Alert variant="destructive" className="border-destructive/30">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Submission Error</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>}

                {/* Primary Contact Section */}
                <div>
                  <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground mb-4">
                    1. Contact Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-name" className="text-sm font-medium">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-name" name="name" type="text" required placeholder="e.g. Jane Miller" value={formData.name} onChange={handleInputChange} disabled={status === "submitting"} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-phone" className="text-sm font-medium">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-phone" name="phone" type="tel" required placeholder="Your phone number" value={formData.phone} onChange={handleInputChange} disabled={status === "submitting"} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-email" className="text-sm font-medium">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-email" name="email" type="email" required placeholder="Your email address" value={formData.email} onChange={handleInputChange} disabled={status === "submitting"} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-address" className="text-sm font-medium">
                        Street Address or ZIP Code <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-address" name="address" type="text" required placeholder="Decatur, GA or 30030" value={formData.address} onChange={handleInputChange} disabled={status === "submitting"} />
                    </div>
                  </div>
                </div>

                {/* Project Scoping Section */}
                <div className="pt-2 border-t border-border">
                  <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground mb-4 mt-6">
                    2. Project Scope
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="estimate-project-type" className="text-sm font-medium">
                        Project Type <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.project_type} onValueChange={val => handleSelectChange("project_type", val)} required disabled={status === "submitting"}>
                        <SelectTrigger id="estimate-project-type" className="w-full">
                          <SelectValue placeholder="Select remodeling type" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypeOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-timing" className="text-sm font-medium">
                        Timing / Start Goal <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.timing} onValueChange={val => handleSelectChange("timing", val)} required disabled={status === "submitting"}>
                        <SelectTrigger id="estimate-timing" className="w-full">
                          <SelectValue placeholder="When would you like to start?" />
                        </SelectTrigger>
                        <SelectContent>
                          {timingOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-budget" className="text-sm font-medium">
                        Anticipated Budget <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                      </Label>
                      <Select value={formData.budget} onValueChange={val => handleSelectChange("budget", val)} disabled={status === "submitting"}>
                        <SelectTrigger id="estimate-budget" className="w-full">
                          <SelectValue placeholder="Select estimated budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estimate-contact-method" className="text-sm font-medium">
                        Preferred Contact Method <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.contact_method} onValueChange={val => handleSelectChange("contact_method", val)} required disabled={status === "submitting"}>
                        <SelectTrigger id="estimate-contact-method" className="w-full">
                          <SelectValue placeholder="How should we reach you?" />
                        </SelectTrigger>
                        <SelectContent>
                          {contactMethodOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Details Section */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <div className="flex items-center justify-between mt-6">
                    <Label htmlFor="estimate-details" className="text-sm font-medium">
                      Project Details <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                    </Label>
                  </div>
                  <Textarea id="estimate-details" name="details" rows={4} placeholder="Describe your vision, dimensions, or specific needs..." value={formData.details} onChange={handleInputChange} disabled={status === "submitting"} className="resize-y" />
                </div>

                {/* Consent Checkbox */}
                <div className="pt-2">
                  <div className="flex items-start gap-3">
                    <Checkbox id="estimate-consent" checked={formData.consent} onCheckedChange={checked => setFormData(prev => ({
                  ...prev,
                  consent: checked === true
                }))} required disabled={status === "submitting"} className="mt-1" />
                    <Label htmlFor="estimate-consent" className="text-xs sm:text-sm text-muted-foreground leading-snug cursor-pointer font-normal">
                      I agree to be contacted about my project by phone, text, or email.
                    </Label>
                  </div>
                </div>

                {/* Submit Action */}
                <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-muted-foreground order-2 sm:order-1">
                    Direct phone inquiries: <a href="tel:4708479076" className="text-foreground underline underline-offset-2 font-medium">(470) 847-9076</a>
                  </p>
                  <Button type="submit" size="lg" disabled={status === "submitting"} className="order-1 sm:order-2 w-full sm:w-auto px-8">
                    {status === "submitting" ? <>
                        <Loader2 className="size-4 animate-spin" />
                        <span>Sending Request...</span>
                      </> : <>
                        <span>Submit Estimate Request</span>
                        <Send className="size-4" />
                      </>}
                  </Button>
                </div>
              </motion.form>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}